import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  doc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { AssetKind, CatalogEntry } from '../assets/catalog';
import { getFirebase } from './firebase';
import { canRenderThumb, renderFileThumb } from './fileThumb';

// One assetOverrides doc - the CURRENT asset for a logical key. Mirrors what the
// game reads in src/assets/overridesSource.ts (key + url + grid).
export type OverrideRecord = {
  key: string;
  url: string;
  storagePath: string;
  versionId: string;
  kind: AssetKind;
  cols?: number;
  rows?: number;
  autoTrim?: boolean;
  updatedBy?: string;
};

// One assetVersions doc - an immutable upload record (history, never deleted).
export type VersionRecord = {
  id: string;
  key: string;
  url: string;
  storagePath: string;
  kind: AssetKind;
  cols?: number;
  rows?: number;
  autoTrim?: boolean;
  uploadedBy?: string;
  uploadedAtMs?: number;
};

export type SheetMeta = { cols?: number; rows?: number; autoTrim?: boolean };

// Firestore doc ids can't contain '/', so the path-based key is encoded. The
// original key is also stored as a field (that's what the game reads).
function encodeKey(key: string): string {
  return key.replace(/\//g, '~');
}

function fileExt(name: string): string {
  const ext = name.split('.').pop();
  return ext && ext !== name ? ext.toLowerCase() : 'bin';
}

// Strips undefined fields - Firestore rejects undefined values.
export function clean<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k as keyof T] = v as T[keyof T];
  }
  return out;
}

// True for raster/vector formats we can preview as <img>. Design source files
// (.psd/.ai/.eps/.pdf) return false - callers show a file placeholder instead.
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;
export function isImageFileName(name: string): boolean {
  return IMAGE_EXT.test(name);
}

// True for video files we can play in a <video> tag. The trailing (\?|#|$) lets
// this match a Storage download URL (which carries ?alt=media&token=...), not
// just a bare filename - the lightbox detects type straight off the url.
const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;
export function isVideoFileName(name: string): boolean {
  return VIDEO_EXT.test(name);
}

// ----- examples (reference images attached to tickets) -----

// Freeform section whose uploads are also offered in the ticket sprite picker.
export const FOR_MAXIM_SECTION = 'For Maxim';

// Reference image uploaded with a ticket. Not a game asset override - free-form
// images grouped by `section` (default "Examples"), shown in the Assets tab.
export type ExampleRecord = {
  id: string;
  name: string;
  url: string;
  storagePath: string;
  section: string;
  uploadedBy?: string;
  ticketId?: string;
  uploadedAtMs?: number;
  // Web-renderable preview for formats the browser can't show in an <img>
  // (.psd/.ai/.pdf). Absent for normal images - the card uses `url` directly.
  thumbUrl?: string;
};

// Uploads a file into the section's Storage folder and returns its object info.
// Per-section subfolder keeps Examples and TODO physically separate in Storage.
// Stored under game-assets/ so the existing Storage rules apply (public read,
// designer write) without a new rule.
async function uploadExampleObject(
  file: File,
  section: string,
): Promise<{ url: string; storagePath: string; name: string }> {
  const { storage } = getFirebase();
  const folder = section.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const id = `${Date.now()}-${Math.round(Math.random() * 1e6)}`;
  const storagePath = `game-assets/${folder}/${id}_${file.name}`;
  const objectRef = ref(storage, storagePath);
  await uploadBytes(objectRef, file, { contentType: file.type });
  const url = await getDownloadURL(objectRef);
  return { url, storagePath, name: file.name };
}

// For formats the browser can't render in an <img> (.psd/.ai/.pdf): rasterize a
// small PNG and upload it alongside, so the grid has something to show.
// Best-effort - returns undefined (no thumb) on any failure, never blocking the
// real upload.
async function maybeUploadThumb(file: File, section: string): Promise<string | undefined> {
  if (!canRenderThumb(file.name)) return undefined;
  try {
    const blob = await renderFileThumb(file);
    if (!blob) return undefined;
    const thumbFile = new File([blob], `${file.name}.thumb.png`, { type: 'image/png' });
    const obj = await uploadExampleObject(thumbFile, section);
    return obj.url;
  } catch {
    return undefined;
  }
}

export async function uploadExample(params: {
  file: File;
  section?: string;
  userEmail: string;
  ticketId?: string;
}): Promise<void> {
  const { file, section = 'Examples', userEmail, ticketId } = params;
  const { db } = getFirebase();
  const obj = await uploadExampleObject(file, section);
  const thumbUrl = await maybeUploadThumb(file, section);
  await addDoc(
    collection(db, 'examples'),
    clean({
      name: obj.name,
      url: obj.url,
      storagePath: obj.storagePath,
      section,
      uploadedBy: userEmail,
      ticketId,
      thumbUrl,
      uploadedAt: serverTimestamp(),
    }),
  );
}

// Replaces the image of an existing example (keeps the same doc id / ticketId /
// section). Old Storage object is kept, like asset versions. Admin-only (rules).
// Returns the new object info so callers can update local state.
export async function replaceExampleImage(params: {
  id: string;
  file: File;
  section: string;
  userEmail: string;
}): Promise<{ url: string; storagePath: string; name: string; thumbUrl?: string }> {
  const { id, file, section, userEmail } = params;
  const { db } = getFirebase();
  const obj = await uploadExampleObject(file, section);
  const thumbUrl = await maybeUploadThumb(file, section);
  await updateDoc(
    doc(db, 'examples', id),
    clean({
      name: obj.name,
      url: obj.url,
      storagePath: obj.storagePath,
      uploadedBy: userEmail,
      // Drop a stale thumb when the replacement has none (e.g. PSD -> image).
      thumbUrl: thumbUrl ?? deleteField(),
      uploadedAt: serverTimestamp(),
    }),
  );
  return { ...obj, thumbUrl };
}

export async function listExamples(): Promise<ExampleRecord[]> {
  const { db } = getFirebase();
  const snap = await getDocs(collection(db, 'examples'));
  const rows = snap.docs.map((d) => {
    const data = d.data() as Omit<ExampleRecord, 'id' | 'uploadedAtMs'> & {
      uploadedAt?: { toMillis?: () => number };
    };
    return { ...data, id: d.id, uploadedAtMs: data.uploadedAt?.toMillis?.() };
  });
  return rows.sort((a, b) => (b.uploadedAtMs ?? Infinity) - (a.uploadedAtMs ?? Infinity));
}

// Removes the Firestore record (the Storage object is kept, like asset versions).
export async function deleteExample(id: string): Promise<void> {
  const { db } = getFirebase();
  await deleteDoc(doc(db, 'examples', id));
}

// ----- admin settings -----

export type SprintConfig = { epochMondayMs: number };

// Reads the sprint epoch config, or null when unset (callers use the default).
export async function getSprintConfig(): Promise<SprintConfig | null> {
  const { db } = getFirebase();
  const snap = await getDoc(doc(db, 'config', 'sprints'));
  const data = snap.data() as { epochMondayMs?: number } | undefined;
  return typeof data?.epochMondayMs === 'number' ? { epochMondayMs: data.epochMondayMs } : null;
}

// Admin-only (enforced by firestore.rules). epochMondayMs must be a Monday
// (the caller snaps it).
export async function setSprintEpoch(epochMondayMs: number, userEmail: string): Promise<void> {
  const { db } = getFirebase();
  await setDoc(doc(db, 'config', 'sprints'), {
    epochMondayMs,
    updatedBy: userEmail,
    updatedAt: serverTimestamp(),
  });
}

export type SoundConfig = { chimeVolume: number };

// Reads the chime volume config, or null when unset (callers use the default).
export async function getSoundConfig(): Promise<SoundConfig | null> {
  const { db } = getFirebase();
  const snap = await getDoc(doc(db, 'config', 'sound'));
  const data = snap.data() as { chimeVolume?: number } | undefined;
  return typeof data?.chimeVolume === 'number' ? { chimeVolume: data.chimeVolume } : null;
}

// Admin-only (enforced by firestore.rules). chimeVolume is 0..1.
export async function setSoundConfig(chimeVolume: number, userEmail: string): Promise<void> {
  const { db } = getFirebase();
  await setDoc(doc(db, 'config', 'sound'), {
    chimeVolume,
    updatedBy: userEmail,
    updatedAt: serverTimestamp(),
  });
}

// Emails on the designers allowlist (doc id = email). Used for the assignee
// filter / picker. Readable by any signed-in user (see firestore.rules).
export async function listDesigners(): Promise<string[]> {
  const { db } = getFirebase();
  const snap = await getDocs(collection(db, 'designers'));
  return snap.docs.map((d) => d.id).sort();
}

export async function listOverrides(): Promise<Map<string, OverrideRecord>> {
  const { db } = getFirebase();
  const snap = await getDocs(collection(db, 'assetOverrides'));
  const map = new Map<string, OverrideRecord>();
  snap.forEach((d) => {
    const data = d.data() as OverrideRecord;
    if (data.key) map.set(data.key, data);
  });
  return map;
}

export async function listVersions(key: string): Promise<VersionRecord[]> {
  const { db } = getFirebase();
  // Filter only - no orderBy, so no composite index is needed. History per
  // asset is small, so we sort newest-first on the client. A just-uploaded doc
  // has a pending serverTimestamp (undefined here); treat it as newest.
  const snap = await getDocs(query(collection(db, 'assetVersions'), where('key', '==', key)));
  const rows = snap.docs.map((d) => {
    const data = d.data() as Omit<VersionRecord, 'id' | 'uploadedAtMs'> & {
      uploadedAt?: { toMillis?: () => number };
    };
    return {
      ...data,
      id: d.id,
      uploadedAtMs: data.uploadedAt?.toMillis?.(),
    };
  });
  return rows.sort((a, b) => (b.uploadedAtMs ?? Infinity) - (a.uploadedAtMs ?? Infinity));
}

// Uploads a new image/audio as a fresh version (old objects are never touched),
// records it in assetVersions, then repoints the assetOverrides doc at it so the
// game picks it up on next load.
export async function uploadAssetVersion(params: {
  entry: CatalogEntry;
  file: File;
  meta?: SheetMeta;
  userEmail: string;
}): Promise<void> {
  const { entry, file, meta, userEmail } = params;
  const { db, storage } = getFirebase();

  const versionId = `${Date.now()}`;
  const storagePath = `game-assets/${encodeKey(entry.key)}/${versionId}.${fileExt(file.name)}`;
  const objectRef = ref(storage, storagePath);
  await uploadBytes(objectRef, file, { contentType: file.type });
  const url = await getDownloadURL(objectRef);

  const grid = entry.kind === 'sheet' ? meta : undefined;

  await addDoc(
    collection(db, 'assetVersions'),
    clean({
      key: entry.key,
      url,
      storagePath,
      kind: entry.kind,
      cols: grid?.cols,
      rows: grid?.rows,
      autoTrim: grid?.autoTrim,
      uploadedBy: userEmail,
      uploadedAt: serverTimestamp(),
    }),
  );

  await setDoc(
    doc(db, 'assetOverrides', encodeKey(entry.key)),
    clean({
      key: entry.key,
      url,
      storagePath,
      versionId,
      kind: entry.kind,
      cols: grid?.cols,
      rows: grid?.rows,
      autoTrim: grid?.autoTrim,
      updatedBy: userEmail,
      updatedAt: serverTimestamp(),
    }),
  );
}

// Repoints the override at an earlier version (history rows stay intact).
export async function rollbackToVersion(params: {
  version: VersionRecord;
  userEmail: string;
}): Promise<void> {
  const { version, userEmail } = params;
  const { db } = getFirebase();
  await setDoc(
    doc(db, 'assetOverrides', encodeKey(version.key)),
    clean({
      key: version.key,
      url: version.url,
      storagePath: version.storagePath,
      versionId: version.id,
      kind: version.kind,
      cols: version.cols,
      rows: version.rows,
      autoTrim: version.autoTrim,
      updatedBy: userEmail,
      updatedAt: serverTimestamp(),
    }),
  );
}
